import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { useAgentChat } from '../hooks/useAgentChat';
import agentService from '../services/agentService';
import toast from './ui/Toast';

const AgentChatInterface = ({ userId, agentId, agentName, agentIcon, agentDescription }) => {
  const [message, setMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  const { response, isLoading, error, sendMessage: sendAgentMessage } = useAgentChat(
    userId,
    agentId,
    currentConversationId,
    true
  );

  useEffect(() => {
    if (userId) {
      loadConversations();
    }
  }, [userId]);

  useEffect(() => {
    if (currentConversationId) {
      loadMessages(currentConversationId);
    }
  }, [currentConversationId]);

  useEffect(() => {
    if (response) {
      // Add streaming response to messages
      setMessages(prev => {
        const lastMessage = prev?.[prev?.length - 1];
        if (lastMessage && lastMessage?.sender === 'agent' && lastMessage?.isStreaming) {
          // Update existing streaming message
          return [
            ...prev?.slice(0, -1),
            { ...lastMessage, content: response }
          ];
        } else {
          // Add new streaming message
          return [
            ...prev,
            {
              id: `temp-${Date.now()}`,
              sender: 'agent',
              content: response,
              created_at: new Date()?.toISOString(),
              isStreaming: true
            }
          ];
        }
      });
      scrollToBottom();
    }
  }, [response]);

  useEffect(() => {
    if (error) {
      toast?.error('Failed to send message', {
        description: error?.message || 'Please try again'
      });
    }
  }, [error]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const { data, error } = await agentService?.getConversations(userId);
      if (error) throw error;
      
      // Filter conversations for this agent
      const agentConversations = (data || [])?.filter(conv => conv?.agent_id === agentId);
      setConversations(agentConversations);
      
      // Auto-select most recent conversation
      if (agentConversations?.length > 0 && !currentConversationId) {
        setCurrentConversationId(agentConversations?.[0]?.id);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const { data, error } = await agentService?.getMessages(conversationId);
      if (error) throw error;
      setMessages(data || []);
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast?.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message?.trim() || isLoading) return;

    const userMessage = message?.trim();
    setMessage('');

    // Add user message to UI immediately
    const tempUserMessage = {
      id: `temp-user-${Date.now()}`,
      sender: 'user',
      content: userMessage,
      created_at: new Date()?.toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);
    scrollToBottom();

    try {
      const result = await sendAgentMessage(userMessage);
      
      if (result?.conversationId) {
        // Update conversation ID if new conversation was created
        if (!currentConversationId) {
          setCurrentConversationId(result?.conversationId);
          await loadConversations();
        }
        
        // Reload messages to get the saved versions
        await loadMessages(result?.conversationId);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove temporary user message on error
      setMessages(prev => prev?.filter(m => m?.id !== tempUserMessage?.id));
    }
  };

  const handleNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full elevated-card rounded-2xl overflow-hidden backdrop-blur-sm border border-border/50">
      {/* Header with Glass Effect */}
      <div className="glass-strong border-b border-border/50">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shadow-lg">
              <Icon name={agentIcon} size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground font-heading">{agentName}</h3>
              <p className="text-sm text-muted-foreground">{agentDescription}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNewConversation}
            iconName="Plus"
            className="glass-subtle hover:glass-strong shadow-sm"
          >
            New Chat
          </Button>
        </div>
      </div>

      {/* Messages Area with Gradient Background */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-br from-background/50 to-muted/20">
        {loadingMessages ? (
          <div className="flex items-center justify-center h-full">
            <div className="glass-strong rounded-2xl p-8">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          </div>
        ) : messages?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="glass-strong rounded-3xl p-8 max-w-md">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mb-6 mx-auto shadow-glass">
                <Icon name={agentIcon} size={40} color="var(--color-primary)" />
              </div>
              <h4 className="text-xl font-bold text-foreground mb-3 font-heading">Start a conversation</h4>
              <p className="text-sm text-muted-foreground">
                Ask me anything about {agentName?.toLowerCase()}. I'm here to help!
              </p>
            </div>
          </div>
        ) : (
          messages?.map((msg) => (
            <div
              key={msg?.id}
              className={`flex ${msg?.sender === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-5 py-3 shadow-lg backdrop-blur-sm transition-smooth hover-lift ${
                  msg?.sender === 'user' ?'bg-gradient-to-br from-primary to-purple-600 text-primary-foreground' :'glass-strong text-foreground'
                }`}
              >
                <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg?.content}</div>
                <div className="text-xs opacity-70 mt-2 font-caption">
                  {new Date(msg.created_at)?.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          ))
        )}
        {isLoading && messages?.[messages?.length - 1]?.sender !== 'agent' && (
          <div className="flex justify-start animate-in fade-in duration-300">
            <div className="glass-strong rounded-2xl px-5 py-3 shadow-lg">
              <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area with Glass Effect */}
      <div className="glass-strong border-t border-border/50 p-6">
        <div className="flex gap-3">
          <textarea
            value={message}
            onChange={(e) => setMessage(e?.target?.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Message ${agentName}...`}
            className="flex-1 resize-none rounded-xl border border-border/50 bg-background/50 backdrop-blur-sm px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 shadow-sm"
            rows={2}
            disabled={isLoading}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!message?.trim() || isLoading}
            iconName={isLoading ? 'Loader2' : 'Send'}
            className={`shadow-lg hover:shadow-xl transition-all duration-300 ${isLoading ? 'animate-spin' : ''}`}
            size="lg"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgentChatInterface;